/**
 * Commit pipeline for post-solve result editing.
 *
 * applyResultAmendmentHelper re-runs the evaluator inside the reducer (pure
 * and deterministic), so the committed rows can never go stale relative to
 * whatever preview the user acknowledged, then materializes:
 *   rows → drops → summary → geoJson rebuild (existing pipeline) → log entry.
 */
import { original } from '@reduxjs/toolkit';

import {
  addGeoJsonResults,
  clearGeoJsonResults,
  generateUUID,
} from 'store/functions/geoJsonFunctions.js';
import { evaluateAmendment } from 'store/functions/resultEditing/amendments';
import {
  buildEvalContext,
  computeResultSummary,
  stopKeyOf,
} from 'store/functions/resultEditing/evaluator';
import { synthesizePathLegs } from 'store/functions/resultEditing/pathSynthesis';

/**
 * Read the plain (non-draft) data object for evaluation: immer draft proxies
 * make the 100k-row travel-cost scan an order of magnitude slower, and the
 * cost-index memoization needs stable references across commits.
 */
const plainData = (state) => original(state)?.data ?? state.data;

const timeUnitOf = (state) => state.generalSettings?.time?.timeUnit || 'clock';

/** Rebuild the result geoJson layers from the CURRENT rows via the existing pipeline. */
function rebuildResultGeoJson(state) {
  const drops = [...(state.data.dropped_locations || []), ...(state.data.dropped_trips || [])];
  state.geoJson = clearGeoJsonResults(state.geoJson);
  const paths = synthesizePathLegs(state.data.route_stops, state.data.locations);
  addGeoJsonResults(state.geoJson, paths, drops, state.data.locations);
  state.geoJsonFilter.dropLayerVisible = drops.length > 0;
}

export const applyResultAmendmentHelper = (state, payload) => {
  const { amendment, overriddenViolationIds = [] } = payload || {};
  if (!amendment || !amendment.type) return;

  const data = plainData(state);
  const ctx = buildEvalContext(data, timeUnitOf(state));
  const result = evaluateAmendment(data, amendment, ctx);
  if (result.error) {
    state.notifications.warning.push({ type: 'warning', message: `Edit failed: ${result.error}` });
    return;
  }

  // A violating edit may only land if every violation was explicitly overridden.
  const overridden = new Set(overriddenViolationIds);
  const unacknowledged = result.violations.filter((v) => !overridden.has(violationId(v)));
  if (unacknowledged.length > 0) {
    state.notifications.warning.push({
      type: 'warning',
      message: 'Edit not applied: it has feasibility warnings that were not acknowledged.',
    });
    return;
  }

  // 1) Rows: swap out the affected vehicles' rows, preserving table order for
  //    the untouched vehicles. Overridden violations mark their rows via the
  //    grid's existing Valid/validationMessage convention.
  const affected = new Set(result.newRowsByVehicle.keys());
  const violationsByStopKey = new Map();
  for (const v of result.violations) {
    const key = v.entities && v.entities.stopKey;
    if (!key) continue;
    if (!violationsByStopKey.has(key)) violationsByStopKey.set(key, []);
    violationsByStopKey.get(key).push(v.message);
  }
  const kept = data.route_stops.filter((r) => !affected.has(String(r.vehicleId)));
  const added = [];
  for (const rows of result.newRowsByVehicle.values()) {
    for (const row of rows) {
      const messages = violationsByStopKey.get(stopKeyOf(row));
      added.push(messages ? { ...row, Valid: false, validationMessage: messages.join('\n') } : row);
    }
  }
  state.data.route_stops = [...kept, ...added];

  // 2) Drops.
  const removeTripIds = new Set(result.dropRemovals.tripIds.map(String));
  const removeLocationIds = new Set(result.dropRemovals.locationIds.map(String));
  state.data.dropped_trips = [
    ...(data.dropped_trips || []).filter((d) => !removeTripIds.has(String(d.tripId))),
    ...result.dropAdds.trips,
  ];
  state.data.dropped_locations = [
    ...(data.dropped_locations || []).filter((d) => !removeLocationIds.has(String(d.locationId))),
    ...result.dropAdds.locations,
  ];

  // 3) Summary — recomputed with the backend's formulas, labeled as adjusted.
  const priorSummary = data.resultSummary;
  const amendmentsSoFar = Array.isArray(data.resultAmendments) ? data.resultAmendments.length : 0;
  state.data.resultSummary = {
    ...computeResultSummary(state.data, priorSummary),
    manuallyAdjusted: true,
    manualAdjustmentCount: amendmentsSoFar + 1,
  };

  // 4) geoJson through the existing result pipeline.
  rebuildResultGeoJson(state);

  // 5) Audit log.
  state.data.resultAmendments = [
    ...(data.resultAmendments || []),
    {
      id: generateUUID(),
      seq: amendmentsSoFar,
      type: amendment.type,
      payload: amendment,
      evaluation: {
        costDelta: result.costDelta,
        violations: result.violations.map((v) => ({ type: v.type, message: v.message, entities: v.entities })),
      },
      overriddenViolationIds,
      createdAt: new Date().toISOString(),
    },
  ];
};

export const revertResultAmendmentsHelper = (state) => {
  const data = plainData(state);
  const baseline = data.resultBaseline;
  if (!baseline || !Array.isArray(baseline.route_stops) || baseline.route_stops.length === 0) return;

  // Fresh stopIds so grid row identity can't collide with the replaced rows.
  state.data.route_stops = baseline.route_stops.map((r) => ({ ...r, stopId: generateUUID() }));
  state.data.dropped_trips = (baseline.dropped_trips || []).map((r) => ({ ...r }));
  state.data.dropped_locations = (baseline.dropped_locations || []).map((r) => ({ ...r }));
  state.data.resultSummary = { ...baseline.resultSummary };
  state.data.resultAmendments = [];
  rebuildResultGeoJson(state);
};

/** Stable id for a violation so overrides can reference it across renders. */
export function violationId(v) {
  const e = v.entities || {};
  return `${v.type}|${e.stopKey ?? ''}|${e.vehicleId ?? ''}|${e.tripId ?? ''}`;
}
